import { apiFetch } from '@/lib/api';

/**
 * Venue comment with user details
 */
export interface VenueComment {
  id: string;
  venue_id: string;
  organization_id: string;
  user_id: string;
  comment_text: string;
  is_edited: boolean;
  edited_at: string | null;
  created_at: string;
  updated_at: string;
  user_name: string | null;
  user_email: string | null;
}

/**
 * Response from list endpoint with pagination
 */
export interface CommentsListResponse {
  items: VenueComment[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

/**
 * Options for listing comments
 */
export interface ListCommentsOptions {
  page?: number;
  pageSize?: number;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Service for venue comments API operations
 * Follows the established pattern from VenuesService.ts
 */
export class VenueCommentsService {
  /**
   * List all comments for a venue
   */
  async list(
    venueId: string,
    options: ListCommentsOptions = {}
  ): Promise<CommentsListResponse> {
    const params = new URLSearchParams({
      ...(options.page && { page: String(options.page) }),
      ...(options.pageSize && { pageSize: String(options.pageSize) }),
      ...(options.sortOrder && { sortOrder: options.sortOrder })
    });

    const url = `/api/venues/${venueId}/comments${params.toString() ? '?' + params : ''}`;
    const response = await apiFetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch comments');
    }
    
    const data = await response.json();
    return data.data;
  }

  /**
   * Create a new comment
   */
  async create(venueId: string, text: string): Promise<{ id: string; comment: VenueComment }> {
    const response = await apiFetch(`/api/venues/${venueId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment_text: text })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: { message: 'Failed to create comment' } }));
      throw new Error(errorData.error?.message || 'Failed to create comment');
    }
    
    const data = await response.json();
    return data.data;
  }

  /**
   * Update an existing comment
   */
  async update(commentId: string, text: string): Promise<VenueComment> {
    const response = await apiFetch(`/api/venues/comments/${commentId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment_text: text })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Failed to update comment');
    }
    
    const data = await response.json();
    return data.data;
  }

  /**
   * Delete a comment
   */
  async delete(commentId: string): Promise<void> {
    const response = await apiFetch(`/api/venues/comments/${commentId}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Failed to delete comment');
    }
  }
}

